import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';

import PageContainer from '../../../shared/PageContainer';
import CyberpunkTitle from '../../../shared/CyberpunkTitle';
import { useColors } from '../../../theme/ColorModeContext';
import IntelOverview from './IntelOverview';
import IntelContent from './IntelContent';
import IntelUsers from './IntelUsers';
import { MONO } from './widgets';

interface Props {
    token: string;
}

const TABS = [
    { key: 'overview', label: 'Growth & engagement' },
    { key: 'content', label: 'Content performance' },
    { key: 'users', label: 'Users' },
] as const;

const WINDOWS = [7, 30, 90, 180, 365];

/**
 * /admin/intel. Tab, window and the admin toggle live in the query string so a
 * view can be bookmarked or shared with another admin.
 */
const AdminIntel = ({ token }: Props) => {
    const colors = useColors();
    const [params, setParams] = useSearchParams();
    const tab = TABS.some((t) => t.key === params.get('tab')) ? (params.get('tab') as string) : 'overview';
    const days = WINDOWS.includes(Number(params.get('days'))) ? Number(params.get('days')) : 90;
    const includeAdmins = params.get('admins') === '1';

    const update = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([key, value]) => (value == null ? next.delete(key) : next.set(key, value)));
        setParams(next, { replace: true });
    };

    return (
        <PageContainer>
            <CyberpunkTitle title="User Intel" subtitle="ADMIN ONLY · GROWTH, RETENTION & LEARNING" mb="20px" />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {TABS.map((t) => (
                        <Button
                            key={t.key}
                            size="small"
                            variant={tab === t.key ? 'contained' : 'outlined'}
                            onClick={() => update({ tab: t.key })}
                            sx={{
                                fontFamily: MONO,
                                fontSize: '0.74rem',
                                textTransform: 'none',
                                ...(tab === t.key ? {} : { borderColor: colors.border.default, color: colors.text.secondary }),
                            }}
                        >
                            {t.label}
                        </Button>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    {tab === 'overview' && (
                        <Select
                            size="small"
                            value={days}
                            onChange={(event) => update({ days: String(event.target.value) })}
                            sx={{ fontFamily: MONO, fontSize: '0.78rem', minWidth: 140 }}
                        >
                            {WINDOWS.map((value) => (
                                <MenuItem key={value} value={value}>Last {value} days</MenuItem>
                            ))}
                        </Select>
                    )}
                    <FormControlLabel
                        control={<Switch size="small" checked={includeAdmins} onChange={(event) => update({ admins: event.target.checked ? '1' : null })} />}
                        label="Include admins"
                        sx={{ '& .MuiFormControlLabel-label': { fontFamily: MONO, fontSize: '0.76rem', color: colors.text.secondary } }}
                    />
                </Box>
            </Box>

            {tab === 'overview' && <IntelOverview token={token} days={days} includeAdmins={includeAdmins} />}
            {tab === 'content' && <IntelContent token={token} includeAdmins={includeAdmins} />}
            {tab === 'users' && <IntelUsers token={token} includeAdmins={includeAdmins} />}
        </PageContainer>
    );
};

export default AdminIntel;
